/**
 * Blog Category Images System
 * Manages category-specific placeholder images and gradient overlays
 */

class CategoryImageSystem {
  constructor() {
    this.categories = {
      'Product Releases & Reviews': {
        gradient: 'linear-gradient(135deg, #0066FF 0%, #0052CC 100%)',
        icon: this.getProductIcon(),
        color: '#0066FF'
      },
      'Safety & Riding Tips': {
        gradient: 'linear-gradient(135deg, #00FF88 0%, #00CC6E 100%)',
        icon: this.getSafetyIcon(),
        color: '#00FF88'
      },
      'Maintenance & Tech': {
        gradient: 'linear-gradient(135deg, #FF6600 0%, #CC5200 100%)',
        icon: this.getTechIcon(),
        color: '#FF6600'
      },
      'Community & Events': {
        gradient: 'linear-gradient(135deg, #9333EA 0%, #7928CA 100%)',
        icon: this.getCommunityIcon(),
        color: '#9333EA'
      },
      'Industry News & Trends': {
        gradient: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
        icon: this.getIndustryIcon(),
        color: '#EC4899'
      }
    };

    this.init();
  }

  init() {
    this.applyPlaceholders();
    this.addImageOverlays();
    this.handleImageErrors();
  }

  /**
   * Apply category-specific placeholders to articles without images
   */
  applyPlaceholders() {
    const articleImages = document.querySelectorAll('[data-category-image]');
    
    articleImages.forEach(img => {
      const category = img.getAttribute('data-category');
      if (!category || !this.categories[category]) return;

      // Check if image failed to load or doesn't exist
      if (!img.src || img.complete && img.naturalHeight === 0) {
        this.createPlaceholder(img, category);
      }
    });
  }

  /**
   * Create placeholder for missing images
   */
  createPlaceholder(element, category) {
    const categoryData = this.categories[category];
    const placeholder = document.createElement('div');
    placeholder.className = 'category-image-placeholder';
    placeholder.style.background = categoryData.gradient;
    placeholder.innerHTML = categoryData.icon;

    // Replace image with placeholder
    if (element.parentNode) {
      element.parentNode.replaceChild(placeholder, element);
    }
  }

  /**
   * Add gradient overlays to existing images based on category
   */
  addImageOverlays() {
    const articleCards = document.querySelectorAll('[data-category]');
    
    articleCards.forEach(card => {
      const category = card.getAttribute('data-category');
      if (!category || !this.categories[category]) return;

      const imageWrapper = card.querySelector('.article-card__image-wrapper, .featured-article-card__image-wrapper');
      if (!imageWrapper) return;

      // Add overlay element if it doesn't exist
      if (!imageWrapper.querySelector('.category-overlay')) {
        const overlay = document.createElement('div');
        overlay.className = 'category-overlay';
        overlay.style.background = `linear-gradient(to bottom, transparent 0%, ${this.categories[category].color}15 100%)`;
        imageWrapper.appendChild(overlay);
      }
    });
  }

  /**
   * Handle image loading errors
   */
  handleImageErrors() {
    const images = document.querySelectorAll('img[data-category]');
    
    images.forEach(img => {
      img.addEventListener('error', () => {
        const category = img.getAttribute('data-category');
        if (category && this.categories[category]) {
          this.createPlaceholder(img, category);
        }
      });
    });
  }

  /**
   * Get category color
   */
  getCategoryColor(category) {
    return this.categories[category]?.color || '#6B7280';
  }

  /**
   * Get category gradient
   */
  getCategoryGradient(category) {
    return this.categories[category]?.gradient || 'linear-gradient(135deg, #E5E7EB 0%, #F8F9FA 100%)';
  }

  // Icon SVGs for each category
  getProductIcon() {
    return `
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M40 10L70 25V55L40 70L10 55V25L40 10Z" stroke="white" stroke-width="2.5" fill="none"/>
        <path d="M40 40L40 70" stroke="white" stroke-width="2.5"/>
        <path d="M10 25L40 40L70 25" stroke="white" stroke-width="2.5"/>
        <circle cx="40" cy="40" r="8" fill="white" opacity="0.3"/>
      </svg>
    `;
  }

  getSafetyIcon() {
    return `
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M40 10L15 22V42C15 56 25 68 40 72C55 68 65 56 65 42V22L40 10Z" stroke="white" stroke-width="2.5" fill="none"/>
        <path d="M30 40L36 46L50 32" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
  }

  getTechIcon() {
    return `
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="40" cy="30" r="12" stroke="white" stroke-width="2.5"/>
        <path d="M40 10v8m0 24v8m20-20h-8m-24 0h-8m17.5-17.5l-5.5 5.5m19 19l-5.5 5.5m0-30l-5.5-5.5m-13 13l-5.5-5.5" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
        <rect x="25" y="50" width="30" height="20" rx="2" stroke="white" stroke-width="2.5"/>
        <path d="M35 60h10" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
      </svg>
    `;
  }

  getCommunityIcon() {
    return `
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <circle cx="40" cy="25" r="8" stroke="white" stroke-width="2.5"/>
        <circle cx="22" cy="48" r="8" stroke="white" stroke-width="2.5"/>
        <circle cx="58" cy="48" r="8" stroke="white" stroke-width="2.5"/>
        <path d="M36 33l-9 9m17-9l9 9" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
        <path d="M40 60v-12m-12 6v-6m24 6v-6" stroke="white" stroke-width="2.5" stroke-linecap="round"/>
      </svg>
    `;
  }

  getIndustryIcon() {
    return `
      <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10 40l10-10 10 10 15-15 10 10 10-10" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        <rect x="10" y="15" width="60" height="50" rx="2" stroke="white" stroke-width="2.5"/>
        <path d="M20 30h5m10 0h5m10 0h5m-35 10h5m10 0h5m10 0h5" stroke="white" stroke-width="2" stroke-linecap="round"/>
      </svg>
    `;
  }
}

// Add CSS for placeholders and overlays
const style = document.createElement('style');
style.textContent = `
  .category-image-placeholder {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease;
  }

  .article-card:hover .category-image-placeholder,
  .featured-article-card:hover .category-image-placeholder {
    transform: scale(1.05);
  }

  .category-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  .article-card:hover .category-overlay,
  .featured-article-card:hover .category-overlay {
    opacity: 1;
  }

  /* Ensure placeholders have proper aspect ratio */
  .category-image-placeholder {
    aspect-ratio: 16 / 9;
  }

  @media screen and (max-width: 749px) {
    .category-image-placeholder svg {
      width: 60px;
      height: 60px;
    }
  }
`;

document.head.appendChild(style);

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.categoryImageSystem = new CategoryImageSystem();
  });
} else {
  window.categoryImageSystem = new CategoryImageSystem();
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CategoryImageSystem;
}



